import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import AdminBlog from './AdminBlog'

export default function Admin() {
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [analytics, setAnalytics] = useState({})
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [activeTab, setActiveTab] = useState('dashboard')

  const token = localStorage.getItem('tredex_token')

  useEffect(() => {
    if (user?.role === 'admin') {
      loadData()
    }
  }, [user])

  const loadData = async () => {
    setLoading(true)
    try {
      const [usersRes, analyticsRes, settingsRes] = await Promise.all([
        fetch('/api/admin/users', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/admin/analytics', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/admin/settings', { headers: { 'Authorization': `Bearer ${token}` } })
      ])
      const usersData = await usersRes.json()
      const analyticsData = await analyticsRes.json()
      const settingsData = await settingsRes.json()
      setUsers(usersData.users || [])
      setAnalytics(analyticsData.analytics || {})
      setSettings(settingsData.settings || {})
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateUser = async (userId, updates) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updates)
      })
      const data = await res.json()
      if (res.ok) {
        setMessage('User updated!')
        loadData()
      } else {
        alert(data.error)
      }
    } catch (err) {
      alert(err.message)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!confirm('Delete this user? This cannot be undone.')) return
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        setMessage('User deleted!')
        loadData()
      }
    } catch (err) {
      alert(err.message)
    }
  }

  const handleSaveSettings = async (e) => {
    e.preventDefault()
    try {
      const formData = new FormData(e.target)
      const data = Object.fromEntries(formData)
      const res = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      })
      if (res.ok) {
        setMessage('Settings saved!')
        loadData()
      }
    } catch (err) {
      alert(err.message)
    }
  }

  if (!user || user.role !== 'admin') {
    return (
      <>
        <Navbar />
        <div className="dashboard-page">
          <div className="container" style={{ textAlign: 'center', paddingTop: '60px' }}>
            <i className="fas fa-shield-alt" style={{ fontSize: '4rem', color: 'var(--text-muted)', marginBottom: '20px' }}></i>
            <h2>Admin Access Required</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>You don't have permission to access this page.</p>
            <Link to="/login" className="btn btn-primary">Admin Login</Link>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Navbar />
      <div className="dashboard-page">
        <div className="container">
          <div className="dash-header">
            <div>
              <h1>Admin Panel</h1>
              <p>Manage users, settings, and monitor platform analytics</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <Link to="/admin/widget" className="btn btn-primary"><i className="fas fa-comment-dots"></i> Widget Platform</Link>
            </div>
          </div>

          {message && <div className="form-status success" style={{ display: 'block', marginBottom: '20px' }}>{message}</div>}

          <div className="admin-tabs">
            <button className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
              <i className="fas fa-chart-pie"></i> Dashboard
            </button>
            <button className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
              <i className="fas fa-users"></i> Users ({users.length})
            </button>
            <button className={`admin-tab ${activeTab === 'blog' ? 'active' : ''}`} onClick={() => setActiveTab('blog')}>
              <i className="fas fa-pen-nib"></i> Blog
            </button>
            <button className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
              <i className="fas fa-cog"></i> Settings
            </button>
          </div>

          {activeTab === 'dashboard' && (
            <div className="analytics-grid">
              <div className="analytics-card">
                <i className="fas fa-users"></i>
                <h3>{analytics.totalUsers || 0}</h3>
                <p>Total Users</p>
              </div>
              <div className="analytics-card">
                <i className="fas fa-crown"></i>
                <h3>{analytics.activeSubscriptions || 0}</h3>
                <p>Active Subscriptions</p>
              </div>
              <div className="analytics-card">
                <i className="fas fa-robot"></i>
                <h3>{analytics.totalAiCalls || 0}</h3>
                <p>Total AI Calls</p>
              </div>
              <div className="analytics-card">
                <i className="fas fa-flask"></i>
                <h3>{analytics.trialUsers || 0}</h3>
                <p>Trial Users</p>
              </div>
              <div className="analytics-card">
                <i className="fas fa-credit-card"></i>
                <h3>{analytics.paidUsers || 0}</h3>
                <p>Paid Users</p>
              </div>
              <div className="analytics-card">
                <i className="fas fa-rupee-sign"></i>
                <h3>₹{analytics.revenue || 0}</h3>
                <p>Estimated Revenue</p>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>AI Calls</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>{u.subscription?.plan || 'none'}</td>
                      <td><span className={`badge ${u.subscription?.status === 'active' ? 'confirmed' : 'pending'}`}>{u.subscription?.status || 'inactive'}</span></td>
                      <td>{u.aiCallsUsed}/{u.aiCallsLimit}</td>
                      <td>{u.role}</td>
                      <td>
                        <div className="admin-actions">
                          {u.role !== 'admin' && (
                            <>
                              <button className="btn btn-sm btn-outline" onClick={() => {
                                const limit = prompt('Set AI call limit:', u.aiCallsLimit)
                                if (limit) handleUpdateUser(u.id, { aiCallsLimit: parseInt(limit) })
                              }} title="Edit"><i className="fas fa-edit"></i></button>
                              <button className="btn btn-sm btn-outline" style={{ borderColor: '#FF5F56', color: '#FF5F56' }} onClick={() => handleDeleteUser(u.id)} title="Delete"><i className="fas fa-trash"></i></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'blog' && (
            <AdminBlog />
          )}

          {activeTab === 'settings' && (
            <div className="profile-card" style={{ maxWidth: '600px' }}>
              <h3><i className="fas fa-cog"></i> Platform Settings</h3>
              <form onSubmit={handleSaveSettings}>
                <div className="form-group">
                  <label>Trial Days</label>
                  <input type="number" name="trialDays" defaultValue={settings.trialDays || 14} required />
                </div>
                <div className="form-group">
                  <label>Trial AI Call Limit</label>
                  <input type="number" name="trialAICalls" defaultValue={settings.trialAICalls || 100} required />
                </div>
                <div className="form-group">
                  <label>Monthly Price (INR)</label>
                  <input type="number" name="monthlyPrice" defaultValue={settings.monthlyPrice || 999} required />
                </div>
                <div className="form-group">
                  <label>Quarterly Price (INR)</label>
                  <input type="number" name="quarterlyPrice" defaultValue={settings.quarterlyPrice || 2499} required />
                </div>
                <div className="form-group">
                  <label>Yearly Price (INR)</label>
                  <input type="number" name="yearlyPrice" defaultValue={settings.yearlyPrice || 7999} required />
                </div>
                <button type="submit" className="btn btn-primary">Save Settings <i className="fas fa-check"></i></button>
              </form>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  )
}
